import { createBrowserClient } from '@supabase/ssr'
import type { SupabaseClient } from '@supabase/supabase-js'

// Define the client type returned by createBrowserClient
type BrowserClient = SupabaseClient<any, 'public', any>

// Singleton instance
let supabaseClient: BrowserClient | null = null

/**
 * Create a Supabase client for browser/client components
 *
 * Best practices:
 * - Singleton pattern to reuse client
 * - Minimal configuration
 * - Let Supabase handle token refresh
 */
export function createClient(): BrowserClient {
  // Return existing client if available
  if (supabaseClient) {
    return supabaseClient
  }

  const url = process.env.NEXT_PUBLIC_SUPABASE_URL
  const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

  if (!url || !key) {
    // During build, these might not be available
    if (typeof window === 'undefined') {
      return {} as BrowserClient
    }
    throw new Error('Missing Supabase environment variables')
  }

  // Create client with optimal configuration for magic links
  supabaseClient = createBrowserClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // Remove flowType to use Supabase's default (pkce with fallback)
      // This allows magic links to work properly
      storageKey: 'supabase-auth' // Consistent storage key
    }
  }) as BrowserClient

  return supabaseClient
}

// Export singleton instance
export const supabase = createClient()